"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { cn, formatTime } from "@/lib/utils";
import { useGlobalConfig, usePayment, useUser, useUserVip } from "@/stores/appStore";
import { TabsContent } from "@/components/ui/tabs"
import { Order } from "@/stores/slices/paymentSlice";
import { MemberLabel } from "@/components/fun-ui/MemberLabel";
import { InfoIcon, LogoAlipayIcon, LogoWechatIcon } from "@/components/fun-ui/icons";
import { Tip } from "@/components/fun-ui/Tip";
import {
  Dialog,
  DialogContent,
  DialogClose,
} from "@/components/fun-ui/Dialog";
import { PaymentTabs } from "./PaymentTabs";
import { PaymentDialogUpgrade } from "./PaymentDialogUpgrade";
import { PaymentDialogRecharge } from "./PaymentDialogRecharge";
import { useWebSocketMessage } from "@/context/WebSocketContext";
import Link from "next/link";
import Loading from "../Loading";
import { QRCodeSVG } from "qrcode.react";
import { useSearchParams } from "next/navigation";
import { PaymentUpgradeSuccessAlert } from "./PaymentUpgradeSuccessAlert";
import dayjs from "dayjs";
import { useRenewalSonnerOpen } from "@/stores/localStore";
import { Suspense } from "react";
import { PaymentType } from "@/stores/slices/commonSlice";
import toast from "react-hot-toast";
import { BALANCES_ORDER_INVOICE_DEEP_LINK } from "@/lib/balancesDeepLink";
import { isPaidVip, PAYMENT_DIALOG_QR_SIZE_PX, resolvePaymentTabs } from "@/lib/payment";

type PaymentDialogProps = {}
type PaymentRequestError = Error & {
  code?: number;
};

function formatYuanDisplay(yuan: number) {
  const r = Math.round(yuan * 100) / 100;
  return Number.isInteger(r) ? String(r) : r.toFixed(2);
}

function PaymentDialogContent({ }: PaymentDialogProps) {
  const searchParams = useSearchParams();
  const debug = !!searchParams.get('debug');
  const [scale, setScale] = useState(1);
  const [orders, setOrders] = useState<Record<string, Order>>({});
  const [urls, setUrls] = useState<Record<string, string>>({});
  const [refreshCounter, setRefreshCounter] = useState(0);
  const [serverOverLimit, setServerOverLimit] = useState(false);
  const [successPaymentType, setSuccessPaymentType] = useState<PaymentType | undefined>(undefined);
  const [upgradePaymentOption, setUpgradePaymentOption] = useState({ renewalDays: 0, paymentPrice: 0 });
  const [upgradePlus3xPaymentOption, setUpgradePlus3xPaymentOption] = useState({ renewalDays: 0, paymentPrice: 0 });
  const [rechargePaymentOption, setRechargePaymentOption] = useState({ paymentPrice: 0 });
  const [upgradeActivedIndex, setUpgradeActivedIndex] = useState<number>(0);
  const [upgradePlus3xActivedIndex, setUpgradePlus3xActivedIndex] = useState<number>(0);
  const [rechargeActivedIndex, setRechargeActivedIndex] = useState<number>(0);

  // Refs for storing mutable values
  const ordersRef = useRef<Record<string, Order>>({});
  const urlsRef = useRef<Record<string, string>>({});
  const isMountedRef = useRef(true);
  const isPaidRef = useRef(false);
  /** 扫码后会清空当前订单缓存，保留最近一次扫码订单号用于 paid 命中 */
  const lastScannedOrderNoRef = useRef<string>('');
  /** 当前弹窗上下文内可接受的支付订单号集合（防多窗口串扰） */
  const localPaymentOrderNosRef = useRef<Set<string>>(new Set());
  const paymentTypeRef = useRef<PaymentType>('upgrade');
  /** 同一 currentKey 下避免 createOrder 并发触发两次（StrictMode / 连续 setState） */
  const orderCreateInFlightKeysRef = useRef<Set<string>>(new Set());

  // Store hooks
  const {
    createOrder,
    getPaymentUrl,
    paymentOpen,
    setPaymentOpen,
    paymentType,
    getPaymentPrice,
    setPaymentType,
    paymentCallbak,
    clearPaymentCallback,
    paymentSuccessOpenWithTitle,
    setPaymentSuccessOpenWithTitle,
    setPaymentPrice,
  } = usePayment();

  const { refreshUserInfo, vipUpgradePreview } = useUser();
  const { vip: isVip, vipType, vipExtra, remainingDays = 0 } = useUserVip();
  const { globalConfig } = useGlobalConfig();
  const paymentTabs = resolvePaymentTabs(globalConfig.value?.paymentTabs);

  const paymentPrice = getPaymentPrice();
  const renewalDays = useMemo(() => {
    if (paymentType === 'upgrade') return upgradePaymentOption.renewalDays;
    if (paymentType === 'upgradePlus3x') return upgradePlus3xPaymentOption.renewalDays;
    return upgradePaymentOption.renewalDays;
  }, [paymentType, upgradePaymentOption.renewalDays, upgradePlus3xPaymentOption.renewalDays]);

  const isOver2Years = useMemo(() => {
    const isUpgradeTab = paymentType === 'upgrade' || paymentType === 'upgradePlus3x';
    if (!isUpgradeTab) {
      return false;
    }
    // 前端超期预判规则：
    // - Plus 会员续费（upgrade）参与校验
    // - Plus 3倍会员续费（upgradePlus3x 且 vipType=plus_3x）参与校验
    // - Plus -> Plus 3倍会员升级不做前端预判，仅依赖后端 30201 兜底返回
    const shouldApplyOverLimitGuard =
      paymentType === 'upgrade'
      || (paymentType === 'upgradePlus3x' && vipType === 'plus_3x');
    if (!shouldApplyOverLimitGuard) {
      return false;
    }
    return (remainingDays + renewalDays) > (25 * 31);
  }, [paymentType, remainingDays, renewalDays, vipType]);
  const isOverLimit = isOver2Years || serverOverLimit;

  const memberSchemeTitle =
    paymentType === 'upgradePlus3x' ? 'Plus 3倍会员' : 'Plus 会员';
  const shouldSkipCreateOrderOnPlusTab =
    paymentType === 'upgrade'
    && isPaidVip(isVip)
    && vipType === 'plus_3x';

  const plusTo3xCreditYuan = useMemo(() => {
    if (paymentType !== 'upgradePlus3x' || vipType !== 'plus') return 0;
    const d = vipUpgradePreview?.totalDeduction;
    if (!d) return 0;
    return d / 100;
  }, [paymentType, vipType, vipUpgradePreview?.totalDeduction]);

  const showPlusTo3xDeductionChip =
    paymentType === 'upgradePlus3x'
    && vipType === 'plus'
    && plusTo3xCreditYuan > 0;
  const isCurrentPlus3xUser = vipType === 'plus_3x';
  const isCurrentPlusUser = isPaidVip(isVip) && vipType === 'plus';

  /** Plus→3x 有抵扣时：支付区展示实付；createOrder 仍用 paymentPrice（档位原价） */
  const plus3xPayableDisplayYuan = useMemo(() => {
    if (!showPlusTo3xDeductionChip || !paymentPrice) return null;
    const opts = vipUpgradePreview?.upgradeOptions;
    if (!opts?.length) return null;
    const opt = opts.find((o) => Math.abs(Number(o.price) - Number(paymentPrice)) < 0.02);
    if (!opt) return null;
    return Math.round((opt.actualAmount / 100) * 100) / 100;
  }, [showPlusTo3xDeductionChip, paymentPrice, vipUpgradePreview?.upgradeOptions]);

  const memberPaymentDisplayYuan =
    showPlusTo3xDeductionChip && plus3xPayableDisplayYuan != null
      ? plus3xPayableDisplayYuan
      : paymentPrice;

  // Generate current key for caching orders and URLs
  const currentKey = useMemo(() => (
    `${paymentType}_${paymentPrice}`
  ), [paymentType, paymentPrice]);

  /** 设计稿基准 1100×760，scale 按 REF 宽高与视口适配 */
  const DIALOG_REF_W = 1100;
  const DIALOG_REF_H = 760;
  const DIALOG_SHELL_H = 760;

  // ==================== 生命周期管理 ====================
  useEffect(() => {
    isMountedRef.current = true;

    const calculateScale = () => {
      const vh = Number(window.innerHeight) || DIALOG_REF_H;
      const vw = Number(window.innerWidth) || DIALOG_REF_W;
      const scale = Math.min(
        (vh - 56) / DIALOG_REF_H,
        (vw - 32) / DIALOG_REF_W,
        1,
      );
      setScale(scale);
    };

    calculateScale();
    window.addEventListener('resize', calculateScale);

    return () => {
      isMountedRef.current = false;
      window.removeEventListener('resize', calculateScale);
    };
  }, []);

  // ==================== 用户信息刷新 ====================
  useEffect(() => {
    if (paymentOpen) {
      refreshUserInfo();
    }
  }, [paymentOpen, refreshUserInfo]);

  // ==================== 同步 paymentType 到 ref ====================
  useEffect(() => {
    paymentTypeRef.current = paymentType;
  }, [paymentType]);

  // ==================== WebSocket 消息处理 ====================
  const handlePaymentMessage = useCallback(async (data: any) => {
    const handlePaymentSuccess = async () => {
      await refreshUserInfo();
      isPaidRef.current = true;
      // 使用 ref 获取最新的 paymentType，避免闭包问题
      const currentPaymentType = paymentTypeRef.current;
      if (currentPaymentType === 'recharge') {
        // 充值没有反馈窗口
        // 显示成功提示
        toast.success('飞豆购买成功');
        // 回调，状态返回
        if (paymentCallbak) {
          paymentCallbak({
            type: 'recharge',
            status: 'success',
          });
          clearPaymentCallback();
        }
      } else {
        // 升级会员反馈窗口
        // 回调在窗口关闭后处理
        setSuccessPaymentType(currentPaymentType);
        setPaymentSuccessOpenWithTitle(true);
      }
      setPaymentOpen(false);
    };

    const { event, orderNo, order_no } = data;
    const currentOrderNo = ordersRef.current[currentKey]?.orderNo;
    const incomingOrderNo = orderNo || order_no;
    if (event === 'paid') {
      // 严格防串窗：paid 必须带订单号，且命中当前弹窗上下文记录的订单号
      if (!paymentOpen || !incomingOrderNo) {
        return;
      }
      const incomingOrderNoStr = String(incomingOrderNo);
      const currentOrderNoStr = currentOrderNo ? String(currentOrderNo) : '';
      const lastScannedOrderNo = lastScannedOrderNoRef.current;
      const isKnownLocalOrder =
        localPaymentOrderNosRef.current.has(incomingOrderNoStr)
        || incomingOrderNoStr === currentOrderNoStr
        || incomingOrderNoStr === lastScannedOrderNo;
      if (!isKnownLocalOrder) {
        return;
      }
      localPaymentOrderNosRef.current.delete(incomingOrderNoStr);
      lastScannedOrderNoRef.current = '';
      // 付款后会关闭窗口
      handlePaymentSuccess();
    } else if (event === 'scanned') {
      if (!paymentOpen || !currentOrderNo) {
        return;
      }
      const scannedOrderNo = incomingOrderNo;
      // 若服务端回传订单号，则只刷新当前订单对应二维码，避免误触发
      if (scannedOrderNo && currentOrderNo && scannedOrderNo !== currentOrderNo) {
        return;
      }
      // 记录本次扫码订单号，供后续 paid 命中（scanned 后会清空 orders 缓存）
      const scannedOrderNoStr = String(scannedOrderNo || currentOrderNo || '');
      if (scannedOrderNoStr) {
        lastScannedOrderNoRef.current = scannedOrderNoStr;
        localPaymentOrderNosRef.current.add(scannedOrderNoStr);
      }
      // 二维码被扫码后作废，清空并触发重新创建订单获取新 payUrl
      setOrders(prev => {
        const newOrders = { ...prev };
        delete newOrders[currentKey];
        return newOrders;
      });
      setUrls(prev => {
        const newUrls = { ...prev };
        delete newUrls[currentKey];
        return newUrls;
      });
      setRefreshCounter(prev => prev + 1);
    } else {
      console.log('WebSocket message:', data);
    }
  }, [paymentCallbak, clearPaymentCallback, currentKey, paymentOpen, refreshUserInfo, setPaymentOpen, setPaymentSuccessOpenWithTitle]);

  useWebSocketMessage(handlePaymentMessage, 'payment');

  // ==================== Refs 同步 ====================
  useEffect(() => {
    ordersRef.current = orders;
  }, [orders]);

  useEffect(() => {
    urlsRef.current = urls;
  }, [urls]);

  // ==================== 订单创建逻辑 ====================
  useEffect(() => {
    setServerOverLimit(false);
  }, [paymentType, paymentPrice, renewalDays]);

  useEffect(() => {
    // 如果刷新计数器不为0，则跳过创建新订单
    if (refreshCounter) { }
    if (orderCreateInFlightKeysRef.current.has(currentKey)) return;
    if (ordersRef.current[currentKey] || !paymentPrice) return;
    if (shouldSkipCreateOrderOnPlusTab) {
      return;
    }
    if (paymentType === 'upgrade') {
      if (isOverLimit) {
        console.log('超出2年限制，不创建订单');
        return;
      }
    }
    if (paymentType === 'upgradePlus3x') {
      if (isOverLimit) {
        console.log('超出2年限制，不创建订单');
        return;
      }
    }

    let price = debug ? 0.01 : paymentPrice; // 调试模式使用0.01元；会员升级 amount 为档位标价（元）

    const createNewOrder = async () => {
      orderCreateInFlightKeysRef.current.add(currentKey);
      try {
        const order = await createOrder(price);
        if (order && isMountedRef.current) {
          if (order.orderNo) {
            localPaymentOrderNosRef.current.add(String(order.orderNo));
          }
          setOrders(prev => ({
            ...prev,
            [currentKey]: order,
          }));
          if (order.payUrl) {
            setUrls(prev => ({
              ...prev,
              [currentKey]: order.payUrl,
            }));
          } else if (order.orderNo) {
            // 兼容后端灰度：若未返回 payUrl，回退旧 prepay 获取逻辑
            const fallbackUrl = await getPaymentUrl(order.orderNo);
            if (fallbackUrl && isMountedRef.current) {
              setUrls(prev => ({
                ...prev,
                [currentKey]: fallbackUrl,
              }));
            }
          }
        }
      } catch (error) {
        const requestError = error as PaymentRequestError;
        if (requestError?.code === 30201) {
          setServerOverLimit(true);
          return;
        }
        console.error('创建订单失败:', error);
      } finally {
        orderCreateInFlightKeysRef.current.delete(currentKey);
      }
    };
    void createNewOrder();
  }, [
    currentKey,
    createOrder,
    getPaymentUrl,
    debug,
    isOverLimit,
    shouldSkipCreateOrderOnPlusTab,
    paymentType,
    paymentPrice,
    upgradePaymentOption,
    upgradePlus3xPaymentOption,
    // 触发创建新订单
    refreshCounter
  ]);

  const {
    resetOpenTime,
  } = useRenewalSonnerOpen();

  // 处理升级支付选项变化的回调
  const handleUpgradePaymentOptionChange = useCallback((option: { renewalDays: number, paymentPrice: number }) => {
    setUpgradePaymentOption(option);
  }, [setUpgradePaymentOption]);

  const handleUpgradePlus3xPaymentOptionChange = useCallback((option: { renewalDays: number, paymentPrice: number }) => {
    setUpgradePlus3xPaymentOption(option);
  }, [setUpgradePlus3xPaymentOption]);

  /** 仅清理支付订单运行时缓存，不影响支付成功弹窗状态 */
  const clearOrderRuntimeCache = useCallback(() => {
    lastScannedOrderNoRef.current = '';
    localPaymentOrderNosRef.current.clear();
    orderCreateInFlightKeysRef.current.clear();
    setOrders({});
    setUrls({});
    setRefreshCounter(0);
  }, []);

  const closeAndReset = useCallback(() => {
    isPaidRef.current = false;
    setSuccessPaymentType(undefined);
    clearOrderRuntimeCache();
    setUpgradePaymentOption({ renewalDays: 0, paymentPrice: 0 });
    setUpgradePlus3xPaymentOption({ renewalDays: 0, paymentPrice: 0 });
    setRechargePaymentOption({ paymentPrice: 0 });
    setUpgradeActivedIndex(0);
    setUpgradePlus3xActivedIndex(0);
    setRechargeActivedIndex(0);
    setPaymentPrice(0, 'upgrade');
    setPaymentPrice(0, 'upgradePlus3x');
    setPaymentPrice(0, 'recharge');
  }, [clearOrderRuntimeCache, setPaymentPrice]);

  // 支付窗关闭即清理订单缓存（不影响支付成功弹窗）
  useEffect(() => {
    if (paymentOpen) {
      return;
    }
    setOrders({});
    setUrls({});
    setRefreshCounter(0);
    lastScannedOrderNoRef.current = '';
    localPaymentOrderNosRef.current.clear();
    orderCreateInFlightKeysRef.current.clear();
  }, [paymentOpen]);

  // ==================== 对话框关闭清理 ====================
  // 只在组件卸载时调用 closeAndReset，避免依赖项变化导致频繁执行
  useEffect(() => {
    return () => {
      if (isMountedRef.current) {
        // 使用 ref 来避免依赖项变化
        isPaidRef.current = false;
        setOrders({});
        setUrls({});
        setRefreshCounter(0);
        setUpgradePaymentOption({ renewalDays: 0, paymentPrice: 0 });
        setUpgradePlus3xPaymentOption({ renewalDays: 0, paymentPrice: 0 });
        setRechargePaymentOption({ paymentPrice: 0 });
        setUpgradeActivedIndex(0);
        setUpgradePlus3xActivedIndex(0);
        setRechargeActivedIndex(0);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // 空依赖数组，只在组件卸载时执行

  // ==================== 对话框状态处理 ====================
  const handleDialogOpenChange = useCallback((open: boolean) => {
    // 避免同值重复设置导致的渲染递归
    if (!!paymentOpen === open) return;
    setPaymentOpen(open);
    if (!open) {
      // 无论何种关闭原因，先清理支付窗口的订单缓存；成功弹窗状态不受影响
      clearOrderRuntimeCache();
    }
    // 没有支付就关闭
    if (!open && !isPaidRef.current) {
      // 回调，状态返回
      if (paymentCallbak) {
        paymentCallbak({
          type: paymentType,
          status: 'cancel',
        });
        clearPaymentCallback();
      }
      closeAndReset();
    }
  }, [paymentCallbak, clearPaymentCallback, paymentOpen, paymentType, clearOrderRuntimeCache, closeAndReset, setPaymentOpen]);

  /** 已是 Plus 3倍会员却停在「Plus 会员」Tab：底栏改为续费引导（稿图） */
  const showPlus3xRenewalGuideOnPlusTab = shouldSkipCreateOrderOnPlusTab;
  const showMemberLeftPayment =
    (paymentType === 'upgrade' || paymentType === 'upgradePlus3x')
    && (!isOverLimit || showPlus3xRenewalGuideOnPlusTab);

  /** 会员 Tab 底栏右侧：开具发票（与扫码区同一行右对齐） */
  const memberInvoiceCol = (
    <div className="pointer-events-auto flex shrink-0 flex-col">
      <div className="inline-flex items-center gap-1">
        <Link
          href={BALANCES_ORDER_INVOICE_DEEP_LINK}
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary hover:underline"
          style={{ fontSize: `${14 * scale}px`, lineHeight: `${20 * scale}px` }}
        >
          开具发票
        </Link>
        <Tip
          label="Findin 飞引支持开具电子普通发票，发票明细支持以下选择：文献检索费/信息服务费/数据服务费/技术服务费。支付成功后，你可以在订单记录-发票管理中进行申请。"
          className="cursor-help"
          contentClassName="max-w-[320px]"
        >
          <InfoIcon size={14} className="text-primary" />
        </Tip>
      </div>
    </div>
  );

  const memberLeftPaymentContent = showMemberLeftPayment ? (
    <div
      className="pointer-events-none flex w-full min-w-0 flex-row flex-nowrap items-end justify-between overflow-visible"
      style={{ gap: `${16 * scale}px` }}
    >
      {showPlus3xRenewalGuideOnPlusTab ? (
        <div className="pointer-events-none min-w-0 flex-1 self-end">
          <div
            className="text-pretty [word-break:keep-all]"
            style={{
              fontFamily: 'MiSans, PingFang SC, Microsoft YaHei, sans-serif',
              minHeight: `${24 * scale}px`,
              lineHeight: `${24 * scale}px`,
              opacity: 1,
              fontSize: `${18 * scale}px`,
              fontWeight: 600,
              color: '#82674D',
            }}
          >
            当前 Plus 3倍会员有效期至：
            {vipExtra?.expireTime ? formatTime(vipExtra.expireTime, 'YYYY-MM-DD') : '—'}
            ，续费请前往
            <button
              type="button"
              onClick={() => setPaymentType('upgradePlus3x')}
              className="inline cursor-pointer border-0 bg-transparent p-0 underline underline-offset-4 decoration-from-font hover:opacity-90 pointer-events-auto"
              style={{
                fontFamily: 'MiSans, PingFang SC, Microsoft YaHei, sans-serif',
                fontSize: `${18 * scale}px`,
                lineHeight: `${24 * scale}px`,
                fontWeight: 600,
                color: '#82674D',
              }}
            >
              Plus 3倍会员
            </button>
          </div>
        </div>
      ) : (
        <div
          className="pointer-events-none flex min-w-0 flex-1 flex-row flex-nowrap items-center overflow-visible"
          style={{ gap: `${18 * scale}px` }}
        >
          <Link
            className="relative block shrink-0 cursor-default"
            style={{
              width: `${PAYMENT_DIALOG_QR_SIZE_PX * scale}px`,
              height: `${PAYMENT_DIALOG_QR_SIZE_PX * scale}px`,
            }}
            target={debug ? '_blank' : ''}
            href={(debug && urls[currentKey]) ? urls[currentKey] : ''}
          >
            {!urls[currentKey] ? (
              <Loading mask size="lg" />
            ) : (
              <QRCodeSVG
                data-value={urls[currentKey]}
                value={urls[currentKey]}
                size={PAYMENT_DIALOG_QR_SIZE_PX * scale}
              />
            )}
          </Link>
          <div
            className="flex min-w-0 flex-1 flex-col justify-between overflow-visible"
            style={{
              height: `${PAYMENT_DIALOG_QR_SIZE_PX * scale}px`,
              minHeight: `${PAYMENT_DIALOG_QR_SIZE_PX * scale}px`,
            }}
          >
            <div className="flex min-w-0 shrink-0 flex-row flex-wrap items-center" style={{ gap: `${8 * scale}px` }}>
              <MemberLabel
                className={cn(
                  'flex shrink-0 flex-row flex-nowrap items-baseline font-semibold whitespace-nowrap',
                )}
              >
                <span style={{
                  fontSize: `${20 * scale}px`,
                  lineHeight: `${28 * scale}px`,
                }}
                >￥</span>
                <span
                  className="proportional-nums"
                  style={{
                    fontSize: `${36 * scale}px`,
                    lineHeight: `${40 * scale}px`,
                  }}
                >{formatYuanDisplay(memberPaymentDisplayYuan)}</span>
              </MemberLabel>
              {showPlusTo3xDeductionChip ? (
                <>
                  <div
                    className="inline-flex shrink-0 items-center whitespace-nowrap text-white"
                    style={{
                      height: `${22 * scale}px`,
                      fontSize: `${12 * scale}px`,
                      lineHeight: `${22 * scale}px`,
                      fontWeight: 500,
                      borderRadius: `${6 * scale}px ${6 * scale}px ${6 * scale}px 0`,
                      opacity: 1,
                      background: 'linear-gradient(90deg, #FF0C27 0%, #FF7341 100%)',
                      paddingLeft: `${8 * scale}px`,
                      paddingRight: `${8 * scale}px`,
                    }}
                  >
                    {`已抵扣未使用的 Plus 会员 ¥ ${formatYuanDisplay(plusTo3xCreditYuan)}`}
                  </div>
                  <Tip
                    label="如果当前已经是 Plus 会员，系统会根据 Plus 会员的剩余时长、权益量、购买的价格等因素综合计算抵扣金额，抵扣金额会在升级 Plus 3倍会员的页面显示出来，支付抵扣后的会员金额差价，即可完成会员升级。"
                    className="pointer-events-auto cursor-help shrink-0"
                    contentClassName="max-w-[320px]"
                  >
                    <InfoIcon size={14} className="text-[#A3866A]" />
                  </Tip>
                </>
              ) : null}
            </div>
            {
              isPaidVip(isVip) && renewalDays > 0 ? (
                <div
                  className="block w-full max-w-none shrink-0 whitespace-nowrap text-pretty [word-break:keep-all]"
                  style={{
                    fontSize: `${14 * scale}px`,
                    lineHeight: `${20 * scale}px`,
                  }}
                >
                  <MemberLabel className="block w-full font-medium leading-snug">
                    {paymentType === 'upgradePlus3x'
                      ? (
                        isCurrentPlus3xUser ? (
                          <>
                            当前 Plus 3倍会员有效期至：{formatTime(vipExtra?.expireTime!, 'YYYY-MM-DD')}
                            ，续费后将延至：
                            {formatTime(dayjs(vipExtra?.expireTime!).add(renewalDays, 'day').toISOString(), 'YYYY-MM-DD')}
                          </>
                        ) : (
                          <>
                            {isCurrentPlusUser ? '升级' : '开通'} Plus 3倍会员后有效期至：
                            {formatTime(dayjs().startOf('day').add(renewalDays, 'day').toISOString(), 'YYYY-MM-DD')}
                          </>
                        )
                      ) : (
                        <>
                          当前 {memberSchemeTitle}有效期至：{formatTime(vipExtra?.expireTime!, 'YYYY-MM-DD')}
                          ，续费后将延至：
                          {formatTime(dayjs(vipExtra?.expireTime!).add(renewalDays, 'day').toISOString(), 'YYYY-MM-DD')}
                        </>
                      )}
                  </MemberLabel>
                </div>
              ) : null
            }
            <div
              className="flex shrink-0 flex-row flex-nowrap items-center whitespace-nowrap text-nowrap"
              style={{
                fontSize: `${14 * scale}px`,
                lineHeight: `${20 * scale}px`,
              }}
            >
              <span className="shrink-0">使用</span>
              <LogoAlipayIcon style={{
                marginLeft: `${4 * scale}px`,
                marginRight: `${4 * scale}px`,
              }} />
              <span className="shrink-0">支付宝App</span>
              <LogoWechatIcon style={{
                marginLeft: `${4 * scale}px`,
                marginRight: `${4 * scale}px`,
              }} />
              <span className="shrink-0">微信App</span>
              <span
                className="shrink-0"
                style={{
                  marginLeft: `${4 * scale}px`,
                }}
              >扫码支付</span>
            </div>
            <div
              className="min-w-0 shrink-0 overflow-visible text-pretty break-words [word-break:keep-all]"
              style={{
                fontSize: `${14 * scale}px`,
                lineHeight: `${20 * scale}px`,
              }}
            >
              <span>购买即表示接受</span>
              <Link
                className="pointer-events-auto text-primary hover:underline"
                target="_blank"
                href="/docs/payment-terms-of-service"
              >《付款服务条款》</Link>
            </div>
          </div>
        </div>
      )}
      {memberInvoiceCol}
    </div>
  ) : null;

  /** 按需充值：扫码+条款与「开具发票」同一行两端对齐，整块置于双栏下方全宽（视觉稿：右下与充值区底对齐） */
  const rechargeLeftPaymentContent =
    paymentType === 'recharge' && !isOverLimit ? (
      <div
        className="pointer-events-none flex w-full min-w-0 flex-row flex-nowrap items-end justify-between overflow-visible"
        style={{ gap: `${16 * scale}px` }}
      >
        <div
          className="pointer-events-none flex min-w-0 flex-1 flex-row flex-nowrap items-center overflow-visible"
          style={{ gap: `${18 * scale}px` }}
        >
          <Link
            className="relative block shrink-0 cursor-default"
            style={{
              width: `${PAYMENT_DIALOG_QR_SIZE_PX * scale}px`,
              height: `${PAYMENT_DIALOG_QR_SIZE_PX * scale}px`,
            }}
            target={debug ? '_blank' : ''}
            href={(debug && urls[currentKey]) ? urls[currentKey] : ''}
          >
            {!urls[currentKey] ? (
              <Loading mask size="lg" />
            ) : (
              <QRCodeSVG
                data-value={urls[currentKey]}
                value={urls[currentKey]}
                size={PAYMENT_DIALOG_QR_SIZE_PX * scale}
              />
            )}
          </Link>
          <div
            className="flex min-w-0 flex-1 flex-col justify-between overflow-visible"
            style={{
              height: `${PAYMENT_DIALOG_QR_SIZE_PX * scale}px`,
              minHeight: `${PAYMENT_DIALOG_QR_SIZE_PX * scale}px`,
            }}
          >
            <div
              className="shrink-0 whitespace-nowrap font-semibold proportional-nums"
              style={{ color: '#2B66F1' }}
            >
              <span style={{
                fontSize: `${18 * scale}px`,
                lineHeight: `${26 * scale}px`,
              }}
              >¥</span>
              <span
                style={{
                  marginLeft: `${6 * scale}px`,
                  fontSize: `${34 * scale}px`,
                  lineHeight: `${40 * scale}px`,
                }}
              >{paymentPrice}</span>
            </div>
            <div
              className="flex shrink-0 flex-row flex-nowrap items-center whitespace-nowrap text-nowrap text-[#3D3D3D]"
              style={{
                fontSize: `${14 * scale}px`,
                lineHeight: `${20 * scale}px`,
              }}
            >
              <span className="shrink-0">使用</span>
              <LogoAlipayIcon style={{
                marginLeft: `${4 * scale}px`,
                marginRight: `${4 * scale}px`,
              }} />
              <span className="shrink-0">支付宝App</span>
              <LogoWechatIcon style={{
                marginLeft: `${4 * scale}px`,
                marginRight: `${4 * scale}px`,
              }} />
              <span className="shrink-0">微信App</span>
              <span
                className="shrink-0"
                style={{
                  marginLeft: `${4 * scale}px`,
                }}
              >扫码支付</span>
            </div>
            <div
              className="min-w-0 shrink-0 overflow-visible text-pretty break-words text-[#6B6B6B] [word-break:keep-all]"
              style={{
                fontSize: `${14 * scale}px`,
                lineHeight: `${20 * scale}px`,
              }}
            >
              <span>购买即表示接受</span>
              <Link
                className="pointer-events-auto text-[#2B66F1] hover:underline"
                target="_blank"
                href="/docs/payment-terms-of-service"
              >《付款服务条款》</Link>
            </div>
          </div>
        </div>
        <div className="pointer-events-auto flex shrink-0 flex-col">
          <div className="inline-flex items-center gap-1">
            <Link
              href={BALANCES_ORDER_INVOICE_DEEP_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#2B66F1] hover:underline"
              style={{ fontSize: `${14 * scale}px`, lineHeight: `${20 * scale}px` }}
            >
              开具发票
            </Link>
            <Tip
              label="Findin 飞引支持开具电子普通发票，发票明细支持以下选择：文献检索费/信息服务费/数据服务费/技术服务费。支付成功后，你可以在订单记录-发票管理中进行申请。"
              className="cursor-help"
              contentClassName="max-w-[320px]"
            >
              <InfoIcon size={14} className="text-[#2B66F1]" />
            </Tip>
          </div>
        </div>
      </div>
    ) : null;

  /** 超出 2 年上限：与扫码支付底栏同一行布局（左文案 + 右开具发票） */
  const memberOverLimitFooter =
    isOverLimit && vipExtra?.expireTime ? (
      <div
        className="pointer-events-none flex w-full min-w-0 flex-row flex-nowrap items-end justify-between overflow-visible"
        style={{ gap: `${16 * scale}px` }}
      >
        <div
          className="pointer-events-none flex min-w-0 flex-1 flex-row flex-nowrap items-start overflow-visible"
          style={{ gap: `${18 * scale}px` }}
        >
          <div
            className="flex min-w-0 flex-1 flex-col"
            style={{ gap: `${8 * scale}px` }}
          >
            <MemberLabel
              className="font-semibold leading-tight"
              style={{
                fontSize: `${30 * scale}px`,
                lineHeight: `${38 * scale}px`,
              }}
            >
              超出会员有效期上限
            </MemberLabel>
            <div
              className="max-w-full text-pretty text-[#6B6B6B] [word-break:keep-all]"
              style={{
                fontSize: `${Math.max(12, 14 * scale)}px`,
                lineHeight: `${Math.max(20, 22 * scale)}px`,
              }}
            >
              <span
                className="text-[#5C5C5C]"
                style={{
                  fontSize: `${12 * scale}px`,
                  fontWeight: 600,
                }}
              >
                当前 {memberSchemeTitle}有效期至：{formatTime(vipExtra.expireTime, 'YYYY年MM月DD日')}。
              </span>
              <br />
              所选会员方案续费后将超出 2 年有效期上限，请更换会员方案或使用一段时间后再续费。
              <br />
              感谢你对 Findin 的喜爱和支持！
            </div>
          </div>
        </div>
        {memberInvoiceCol}
      </div>
    ) : null;

  // ==================== 渲染逻辑 ====================
  return (
    <>
      <Dialog open={!!paymentOpen} onOpenChange={handleDialogOpenChange}>
        <DialogContent
          surface="transparent"
          showCloseButton
          closeClassName="outline-none"
          closeStyle={{
            top: `${14 * scale}px`,
            right: `${14 * scale}px`,
          }}
          className={cn(
            'box-border !flex !max-w-none min-h-0 w-full flex-col gap-0 p-0',
            'border-none outline-none shadow-lg sm:max-w-none',
            'overflow-x-visible overflow-y-hidden',
            'font-sans',
          )}
          style={{
            display: 'flex',
            flexDirection: 'column',
            width: `min(${DIALOG_REF_W * scale}px, calc(100vw - 2rem))`,
            height: `min(${DIALOG_SHELL_H * scale}px, calc(100vh - 2.5rem))`,
            padding: 0,
            marginLeft: 0,
            marginRight: 0,
            gap: 0,
            paddingTop: `${32 * scale}px`,
            paddingBottom: `${20 * scale}px`,
            borderRadius: `${24 * scale}px`,
            background: 'linear-gradient(180deg, #ECF1FF 0%, #FDFEFF 100%)',
            fontFamily: 'var(--font-misans)',
          }}
          tabIndex={-1}
        // onPointerDownOutside={(evt) => evt.preventDefault()}
        // onOpenAutoFocus={(evt) => evt.preventDefault()}
        >
          <div
            className={cn(
              'flex min-h-0 w-full min-w-0 flex-1 flex-col overflow-x-visible overflow-y-hidden',
            )}
          >
            <PaymentTabs scale={scale} className="min-h-0 min-w-0 flex-1 overflow-x-visible overflow-y-hidden">
              {/* 升级选项卡内容 */}
              {paymentTabs.some((tab: { value: PaymentType }) => tab.value === 'upgrade') && (
                <TabsContent
                  key="upgrade"
                  value="upgrade"
                  className="mt-0 flex min-h-0 flex-1 flex-col overflow-x-hidden overflow-y-hidden focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0 data-[state=inactive]:hidden"
                >
                  <PaymentDialogUpgrade
                    scale={scale}
                    paymentTabType="upgrade"
                    isActive={paymentType === 'upgrade'}
                    activedIndex={upgradeActivedIndex}
                    onActivedIndexChange={setUpgradeActivedIndex}
                    onPaymentOptionChange={handleUpgradePaymentOptionChange}
                    leftColumnFooter={
                      paymentType === 'upgrade'
                        ? (
                          showPlus3xRenewalGuideOnPlusTab
                            ? memberLeftPaymentContent
                            : (isOverLimit ? memberOverLimitFooter : memberLeftPaymentContent)
                        )
                        : null
                    }
                  />
                </TabsContent>
              )}
              {paymentTabs.some((tab: { value: PaymentType }) => tab.value === 'upgradePlus3x') && (
                <TabsContent
                  key="upgradePlus3x"
                  value="upgradePlus3x"
                  className="mt-0 flex min-h-0 flex-1 flex-col overflow-x-hidden overflow-y-hidden focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0 data-[state=inactive]:hidden"
                >
                  <PaymentDialogUpgrade
                    scale={scale}
                    paymentTabType="upgradePlus3x"
                    isActive={paymentType === 'upgradePlus3x'}
                    activedIndex={upgradePlus3xActivedIndex}
                    onActivedIndexChange={setUpgradePlus3xActivedIndex}
                    onPaymentOptionChange={handleUpgradePlus3xPaymentOptionChange}
                    leftColumnFooter={
                      paymentType === 'upgradePlus3x'
                        ? (isOverLimit ? memberOverLimitFooter : memberLeftPaymentContent)
                        : null
                    }
                  />
                </TabsContent>
              )}
              {/* 充值选项卡内容 */}
              {paymentTabs.some((tab: { value: PaymentType }) => tab.value === 'recharge') && (
                <TabsContent
                  key="recharge"
                  value="recharge"
                  className="mt-0 flex min-h-0 flex-1 flex-col overflow-x-visible overflow-y-hidden focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0 data-[state=inactive]:hidden"
                >
                  <PaymentDialogRecharge
                    scale={scale}
                    isActive={paymentType === 'recharge'}
                    activedIndex={rechargeActivedIndex}
                    onActivedIndexChange={setRechargeActivedIndex}
                    onPaymentOptionChange={setRechargePaymentOption}
                    leftColumnFooter={rechargeLeftPaymentContent}
                  />
                </TabsContent>
              )}
            </PaymentTabs>
          </div>
        </DialogContent>
      </Dialog>

      <PaymentUpgradeSuccessAlert
        title={typeof paymentSuccessOpenWithTitle === 'string' ? paymentSuccessOpenWithTitle : ''}
        paymentType={successPaymentType || paymentType}
        open={
          !!paymentSuccessOpenWithTitle
          && (successPaymentType === 'upgrade' || successPaymentType === 'upgradePlus3x')
        }
        setOpen={(open) => {
          if (!open) {
            // 回调，状态返回
            if (paymentCallbak) {
              paymentCallbak({
                type: (successPaymentType || paymentType) as PaymentType,
                status: 'success',
              });
              clearPaymentCallback();
            }
            resetOpenTime();
            closeAndReset();
          }
          if (!open) {
            setSuccessPaymentType(undefined);
          }
          setPaymentSuccessOpenWithTitle(open);
        }}
      />
    </>
  );
}

export function PaymentDialog({ }: PaymentDialogProps) {
  return (
    <Suspense fallback={null}>
      <PaymentDialogContent />
    </Suspense>
  );
}
